import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, ChevronLeft, Check, Phone, Mail, Building2, 
  Users, TrendingUp, Shield, Clock, Gift, Camera, Ruler,
  Send, MapPin, User, FileText, Handshake, Star, Euro, Target, MousePointer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';
import { getVersionString } from '../config/version';
import { LOGO_DARK_BG } from '../config/branding';

const VertriebPraesentationPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    // Firmendaten
    company_name: '',
    company_type: '',
    street: '',
    zip: '',
    city: '',
    // Ansprechpartner
    contact_first_name: '',
    contact_last_name: '',
    contact_position: '',
    email: '',
    phone: '',
    // Plane
    plane_width: '',
    plane_height: '',
    plane_location: '',
    plane_photo_permission: false,
    // Vertriebler
    sales_rep_name: '',
    sales_rep_notes: '',
    // Zustimmung
    interested: false,
    contact_later: false,
    terms_accepted: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const formRef = useRef(null);

  const slides = [
    {
      id: 'intro',
      title: 'Willkommen bei',
      subtitle: 'Dein Gutachten Direkt',
      content: (
        <div className="text-center">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/20d8f41f-7a77-4ac0-bb68-7bf063e920fc/images/47faaa2fcbc118000ee7ab1147e393d613fcee55b1059aefdddedf130acc84fc.png"
            alt="Partnerschaft"
            className="w-full max-w-2xl mx-auto rounded-2xl shadow-2xl mb-8"
          />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ihr Partner für Unfallgutachten
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Wir zeigen Ihnen, wie Sie mit uns <strong>zusätzliche Einnahmen</strong> generieren 
            und gleichzeitig Ihren <strong>Kundenservice verbessern</strong>.
          </p>
        </div>
      )
    },
    {
      id: 'problem',
      title: 'Das Problem',
      subtitle: 'Was passiert nach einem Unfall?',
      content: (
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
              <h3 className="font-bold text-red-800 mb-2">Für Ihre Kunden:</h3>
              <ul className="text-red-700 space-y-2">
                <li>• Unsicherheit nach dem Unfall</li>
                <li>• Kein Wissen über ihre Rechte</li>
                <li>• Versicherungen kürzen Zahlungen</li>
                <li>• Viele verlieren Geld durch fehlende Gutachten</li>
              </ul>
            </div>
            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
              <h3 className="font-bold text-orange-800 mb-2">Für Ihren Betrieb:</h3>
              <ul className="text-orange-700 space-y-2">
                <li>• Kunden fragen nach Empfehlungen</li>
                <li>• Keine Zeit für Gutachter-Recherche</li>
                <li>• Kein Zusatzverdienst möglich</li>
              </ul>
            </div>
          </div>
          <div className="bg-gray-100 rounded-2xl p-8 text-center">
            <div className="text-6xl font-bold text-red-500 mb-4">73%</div>
            <p className="text-lg text-gray-700">
              der Unfallgeschädigten verzichten auf ein Gutachten – 
              und verlieren dadurch im Schnitt <strong>800-1.500€</strong>
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'solution',
      title: 'Die Lösung',
      subtitle: 'Dein Gutachten Direkt',
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-brand-green/10 rounded-2xl p-6 text-center">
              <div className="w-16 h-16 bg-brand-green rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Schnell</h3>
              <p className="text-gray-600">Termine innerhalb von 24-48 Stunden</p>
            </div>
            <div className="bg-brand-green/10 rounded-2xl p-6 text-center">
              <div className="w-16 h-16 bg-brand-green rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Unabhängig</h3>
              <p className="text-gray-600">Keine Bindung an Versicherungen</p>
            </div>
            <div className="bg-brand-green/10 rounded-2xl p-6 text-center">
              <div className="w-16 h-16 bg-brand-green rounded-full flex items-center justify-center mx-auto mb-4">
                <Euro className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Kostenlos</h3>
              <p className="text-gray-600">Für den Geschädigten bei unverschuldetem Unfall</p>
            </div>
          </div>
          <div className="bg-gray-900 text-white rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-4 text-center">Über 10 Jahre Erfahrung</h3>
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-brand-green">10+</div>
                <div className="text-gray-400">Jahre Erfahrung</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-brand-green">Bundesweit</div>
                <div className="text-gray-400">Einsatzgebiet</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-brand-green">100%</div>
                <div className="text-gray-400">Gerichtsfest</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-brand-green">24-48h</div>
                <div className="text-gray-400">Terminvergabe</div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'benefits',
      title: 'Ihre Vorteile',
      subtitle: 'Als Partner profitieren Sie mehrfach',
      content: (
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-lg border-l-4 border-brand-green">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Euro className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Zusätzliche Einnahmen</h3>
                <p className="text-gray-600">Bis zu 50% Provision vom Grundhonorar – ohne eigenen Aufwand</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-lg border-l-4 border-brand-green">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Besserer Kundenservice</h3>
                <p className="text-gray-600">Bieten Sie Ihren Kunden einen Rundum-Service nach dem Unfall</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-lg border-l-4 border-brand-green">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Handshake className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Kundenbindung</h3>
                <p className="text-gray-600">Zufriedene Kunden kommen wieder und empfehlen Sie weiter</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-brand-green to-green-700 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-6">Rechenbeispiel</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-white/20 pb-3">
                <span>Ø Grundhonorar pro Fall:</span>
                <span className="font-bold">500 €</span>
              </div>
              <div className="flex justify-between items-center border-b border-white/20 pb-3">
                <span>Ihre Provision (bis zu 50%):</span>
                <span className="font-bold">250 €</span>
              </div>
              <div className="flex justify-between items-center border-b border-white/20 pb-3">
                <span>Bei 3 Fällen/Monat:</span>
                <span className="font-bold">750 €</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-xl">Pro Jahr:</span>
                <span className="text-3xl font-bold">9.000 €</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'howto',
      title: 'So funktioniert es',
      subtitle: 'Der komplette Ablauf',
      content: (
        <div className="space-y-8">
          {/* 5-Schritte Prozess */}
          <div className="grid md:grid-cols-5 gap-4">
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-brand-green rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg z-10">1</div>
              <div className="bg-white rounded-xl p-5 pt-8 shadow-lg border-2 border-gray-100 h-full">
                <h3 className="text-base font-bold text-gray-900 mb-2">Kunde kommt zu Ihnen</h3>
                <p className="text-gray-600 text-sm">
                  Nach einem Unfall bringt der Kunde sein Fahrzeug zu Ihnen.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-brand-green rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg z-10">2</div>
              <div className="bg-white rounded-xl p-5 pt-8 shadow-lg border-2 border-gray-100 h-full">
                <h3 className="text-base font-bold text-gray-900 mb-2">Ihr Mitarbeiter nimmt auf</h3>
                <p className="text-gray-600 text-sm">
                  Ein von uns geschulter Mitarbeiter dokumentiert den Schaden im Portal mit Fotos & Infos.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-brand-green rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg z-10">3</div>
              <div className="bg-white rounded-xl p-5 pt-8 shadow-lg border-2 border-gray-100 h-full">
                <h3 className="text-base font-bold text-gray-900 mb-2">Wir erstellen das Gutachten</h3>
                <p className="text-gray-600 text-sm">
                  Unsere Sachverständigen erstellen ein gerichtsfestes Gutachten.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-brand-green rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg z-10">4</div>
              <div className="bg-white rounded-xl p-5 pt-8 shadow-lg border-2 border-gray-100 h-full">
                <h3 className="text-base font-bold text-gray-900 mb-2">Versicherung reguliert</h3>
                <p className="text-gray-600 text-sm">
                  Das Gutachten geht an die gegnerische Versicherung. Der Schaden wird reguliert.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-brand-green rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg z-10">5</div>
              <div className="bg-white rounded-xl p-5 pt-8 shadow-lg border-2 border-brand-green h-full">
                <h3 className="text-base font-bold text-gray-900 mb-2">Provision auf Ihr Konto</h3>
                <p className="text-gray-600 text-sm">
                  Nach erfolgreicher Regulierung überweisen wir Ihre Provision direkt auf Ihr Konto.
                </p>
              </div>
            </div>
          </div>

          {/* Was Sie tun vs. Was wir tun */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-brand-green/10 rounded-2xl p-6 border-2 border-brand-green/30">
              <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-brand-green" />
                Was Sie tun (ca. 15 Min.):
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
                  <span>Kunden empfangen und beraten</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
                  <span>Fotos vom Schaden machen</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
                  <span>Fall im Portal hochladen</span>
                </li>
              </ul>
            </div>
            <div className="bg-blue-50 rounded-2xl p-6 border-2 border-blue-200">
              <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Was wir übernehmen:
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Mitarbeiter-Schulung (kostenlos)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Gutachten-Erstellung</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Kommunikation mit der Versicherung</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Provisions-Auszahlung</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'portal',
      title: 'Das Partner-Portal',
      subtitle: 'So einfach ist die Aufnahme',
      content: (
        <div className="space-y-6">
          {/* Portal Steps */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Step 1: Dashboard */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-gray-100">
              <div className="bg-brand-green text-white px-4 py-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-white text-brand-green rounded-full flex items-center justify-center font-bold text-sm">1</span>
                <span className="font-semibold">Dashboard öffnen</span>
              </div>
              <div className="p-4">
                <div className="bg-gray-100 rounded-lg p-4 mb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-brand-green/20 rounded flex items-center justify-center">
                      <FileText className="w-4 h-4 text-brand-green" />
                    </div>
                    <span className="font-bold text-gray-800">Partner Portal</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-gray-500">Fälle</div>
                      <div className="font-bold">0</div>
                    </div>
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-gray-500">Provision</div>
                      <div className="font-bold text-brand-green">bis zu 50%</div>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600">Übersichtliches Dashboard mit allen Ihren Fällen und Provisionen.</p>
              </div>
            </div>

            {/* Step 2: Auftragsdaten */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-gray-100">
              <div className="bg-brand-green text-white px-4 py-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-white text-brand-green rounded-full flex items-center justify-center font-bold text-sm">2</span>
                <span className="font-semibold">Auftragsdaten eingeben</span>
              </div>
              <div className="p-4">
                <div className="bg-gray-100 rounded-lg p-4 mb-3 space-y-2">
                  <div className="bg-white rounded p-2">
                    <div className="text-xs text-gray-500">Kennzeichen</div>
                    <div className="font-mono text-sm">HH AB 1234</div>
                  </div>
                  <div className="bg-white rounded p-2">
                    <div className="text-xs text-gray-500">Schadentag</div>
                    <div className="font-mono text-sm">20.02.2026</div>
                  </div>
                  <div className="bg-white rounded p-2">
                    <div className="text-xs text-gray-500">Ort</div>
                    <div className="font-mono text-sm">Hamburg</div>
                  </div>
                </div>
                <p className="text-sm text-gray-600">Grunddaten zum Unfall in wenigen Sekunden erfassen.</p>
              </div>
            </div>

            {/* Step 3: Fahrzeugdaten */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-gray-100">
              <div className="bg-brand-green text-white px-4 py-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-white text-brand-green rounded-full flex items-center justify-center font-bold text-sm">3</span>
                <span className="font-semibold">Fahrzeug & Fotos</span>
              </div>
              <div className="p-4">
                <div className="bg-gray-100 rounded-lg p-4 mb-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white rounded p-2">
                      <div className="text-xs text-gray-500">Hersteller</div>
                      <div className="font-mono text-sm">VW</div>
                    </div>
                    <div className="bg-white rounded p-2">
                      <div className="text-xs text-gray-500">Modell</div>
                      <div className="font-mono text-sm">Golf</div>
                    </div>
                  </div>
                  <div className="bg-white rounded p-2 flex items-center gap-2">
                    <Camera className="w-4 h-4 text-brand-green" />
                    <span className="text-sm">Fotos hochladen</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600">Fahrzeugdaten ergänzen und Schadensfotos hochladen.</p>
              </div>
            </div>
          </div>

          {/* Bottom Info */}
          <div className="bg-gradient-to-r from-brand-green/10 to-green-100 rounded-2xl p-6 border-2 border-brand-green/30">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-brand-green rounded-2xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-xl mb-1">Gesamtdauer: Ca. 15 Minuten</h3>
                <p className="text-gray-700">
                  Ihr geschulter Mitarbeiter kann den Fall direkt vor Ort aufnehmen – 
                  mit Smartphone oder Tablet. Wir übernehmen den Rest!
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'offer',
      title: 'Unser Angebot',
      subtitle: '6 Monate kostenlos testen',
      content: (
        <div className="space-y-8">
          <div className="bg-gradient-to-r from-brand-green to-green-600 rounded-2xl p-8 text-white text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-4">
              <Gift className="w-5 h-5" />
              <span className="font-semibold">Exklusives Angebot</span>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-4">6 Monate kostenfrei testen</h3>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Starten Sie ohne Risiko. Keine Kosten, keine Verpflichtung. 
              Nach 6 Monaten entscheiden Sie, ob Sie weitermachen möchten.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Check className="w-6 h-6 text-brand-green" />
                Was Sie erhalten:
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                  <span>Zugang zum Partner-Portal</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                  <span>Bis zu 50% Provision auf alle Gutachten</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                  <span>Kostenlose Werbematerialien</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                  <span>Persönlicher Ansprechpartner</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                  <span>Schulung & Einarbeitung</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Star className="w-6 h-6 text-brand-green" />
                Nach 6 Monaten:
              </h3>
              <p className="text-gray-700 mb-4">
                Sie entscheiden selbst, wie es weitergeht:
              </p>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="font-bold text-gray-900">Starter – 29,99€/Monat</div>
                  <div className="text-gray-600">Bis zu 50% Provision</div>
                </div>
                <div className="bg-brand-green/10 rounded-lg p-4 border-2 border-brand-green">
                  <div className="font-bold text-gray-900">Pro – 49,99€/Monat</div>
                  <div className="text-gray-600">40% Provision ⭐ Beliebt</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="font-bold text-gray-900">Premium – 79,99€/Monat</div>
                  <div className="text-gray-600">50% Provision</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'werbung',
      title: 'Außenwerbung',
      subtitle: 'Kostenlose Werbebanner für Ihren Betrieb',
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/20d8f41f-7a77-4ac0-bb68-7bf063e920fc/images/7089b19de2460cf84607296a595f771b318d949731cdbb296df14a77ebc15f4d.png"
                alt="Beispiel Werbebanner"
                className="rounded-2xl shadow-2xl w-full"
              />
              <p className="text-center text-gray-500 mt-2 text-sm">Beispiel einer Außenwerbung</p>
            </div>
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">So funktioniert es:</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-brand-green rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                  <div>
                    <h4 className="font-bold text-gray-900">Maße mitteilen</h4>
                    <p className="text-gray-600">Sie geben uns die gewünschten Maße für Ihre Werbefläche</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-brand-green rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                  <div>
                    <h4 className="font-bold text-gray-900">Wir erstellen & versenden</h4>
                    <p className="text-gray-600">Wir produzieren die Plane und schicken sie Ihnen kostenlos zu</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-brand-green rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                  <div>
                    <h4 className="font-bold text-gray-900">Aufhängen & Foto senden</h4>
                    <p className="text-gray-600">Sie hängen die Plane auf und senden uns ein Foto</p>
                  </div>
                </div>
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                <p className="text-yellow-800 text-sm">
                  <strong>Hinweis:</strong> Bei Beendigung der Partnerschaft bitten wir um Rücksendung 
                  der Plane auf Ihre Kosten.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'google-ads',
      title: 'Noch mehr Kunden',
      subtitle: 'Wir schalten Google Ads für Sie',
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold">Lokale Google Werbung</h3>
                </div>
                <p className="text-blue-100">
                  Wir schalten professionelle Google Ads Kampagnen, die Unfallgeschädigte 
                  in Ihrer Region direkt zu Ihnen führen.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-lg border-l-4 border-blue-500">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Geografisches Targeting</h4>
                    <p className="text-gray-600 text-sm">Werbung nur in Ihrem Einzugsgebiet – keine Streuverluste</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-lg border-l-4 border-blue-500">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MousePointer className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Keywords wie "Unfallgutachten + [Ihre Stadt]"</h4>
                    <p className="text-gray-600 text-sm">Genau dann sichtbar, wenn Kunden nach Hilfe suchen</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-lg border-l-4 border-blue-500">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Euro className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Für Sie kostenlos</h4>
                    <p className="text-gray-600 text-sm">Wir übernehmen die Werbekosten – Sie profitieren von mehr Kunden</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-900 rounded-2xl p-6 text-white">
              <div className="bg-white/10 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 bg-yellow-400 text-black text-xs font-bold rounded">Anzeige</span>
                </div>
                <h4 className="text-lg font-bold text-blue-400 mb-1">
                  Unfallgutachten Cuxhaven | Kostenlos bei unverschuldetem Unfall
                </h4>
                <p className="text-green-400 text-sm mb-2">www.dein-gutachten-direkt.de/cuxhaven</p>
                <p className="text-gray-300 text-sm">
                  Kfz-Sachverständige vor Ort ✓ Termin in 24h ✓ 100% kostenlos für Sie ✓ 
                  Direkte Abwicklung mit Versicherung
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Durchschnittliche Klickrate:</span>
                  <span className="text-brand-green font-bold">4.2%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Conversion Rate:</span>
                  <span className="text-brand-green font-bold">12%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Kosten für Sie:</span>
                  <span className="text-brand-green font-bold">0 €</span>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t border-white/20">
                <p className="text-center text-gray-400 text-sm">
                  Mehr Kunden = Mehr Provisionen für Sie
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'abschluss',
      title: 'Bereit zu starten?',
      subtitle: 'Werden Sie jetzt Partner',
      content: (
        <div className="text-center space-y-8">
          <div className="max-w-2xl mx-auto">
            <div className="w-24 h-24 bg-brand-green rounded-full flex items-center justify-center mx-auto mb-6">
              <Handshake className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Lassen Sie uns gemeinsam starten!
            </h3>
            <p className="text-xl text-gray-600 mb-8">
              Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden bei Ihnen.
            </p>
          </div>
          <Button 
            onClick={() => setShowForm(true)}
            size="lg"
            className="bg-brand-green hover:bg-brand-green-dark text-white text-xl px-12 py-8 rounded-2xl font-bold shadow-xl"
          >
            Jetzt Partner werden
            <ChevronRight className="w-6 h-6 ml-2" />
          </Button>
          <p className="text-gray-500">
            Keine Kosten • Keine Verpflichtung • 6 Monate kostenlos
          </p>
        </div>
      )
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vertrieb/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Übermittlung fehlgeschlagen');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-green to-green-700 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl p-12 max-w-lg text-center shadow-2xl">
          <div className="w-20 h-20 bg-brand-green rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Vielen Dank!</h1>
          <p className="text-lg text-gray-600 mb-6">
            Die Daten wurden erfolgreich übermittelt. Wir melden uns innerhalb von 24 Stunden.
          </p>
          <Button 
            onClick={() => {
              setSubmitted(false);
              setShowForm(false);
              setCurrentSlide(0);
              setFormData({
                company_name: '', company_type: '', street: '', zip: '', city: '',
                contact_first_name: '', contact_last_name: '', contact_position: '',
                email: '', phone: '', plane_width: '', plane_height: '', plane_location: '',
                plane_photo_permission: false, sales_rep_name: '', sales_rep_notes: '',
                interested: false, contact_later: false, terms_accepted: false
              });
            }}
            className="bg-brand-green hover:bg-brand-green-dark text-white"
          >
            Neue Präsentation starten
          </Button>
        </div>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <Helmet>
          <title>Partner-Daten erfassen | Dein Gutachten Direkt</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-2xl font-bold text-gray-900">Partner-Daten erfassen</h1>
              <Button 
                variant="outline" 
                onClick={() => setShowForm(false)}
                className="text-gray-600"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Zurück zur Präsentation
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Firmendaten */}
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-brand-green" />
                  Firmendaten
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Firmenname *</label>
                    <input
                      type="text"
                      required
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      placeholder="Autohaus Müller GmbH"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Betriebsart *</label>
                    <select
                      required
                      value={formData.company_type}
                      onChange={(e) => setFormData({...formData, company_type: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="werkstatt_frei">Freie Werkstatt</option>
                      <option value="werkstatt_marke">Markengebundene Werkstatt</option>
                      <option value="autohaus">Autohaus</option>
                      <option value="lackiererei">Lackiererei</option>
                      <option value="abschleppdienst">Abschleppdienst</option>
                      <option value="kfz_gutachter">Kfz Gutachter</option>
                      <option value="sonstiges">Sonstiges</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Straße & Hausnummer *</label>
                  <input
                    type="text"
                    required
                    value={formData.street}
                    onChange={(e) => setFormData({...formData, street: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    placeholder="Musterstraße 123"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PLZ *</label>
                    <input
                      type="text"
                      required
                      value={formData.zip}
                      onChange={(e) => setFormData({...formData, zip: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      placeholder="12345"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ort *</label>
                    <input
                      type="text"
                      required
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      placeholder="Hamburg"
                    />
                  </div>
                </div>
              </div>

              {/* Ansprechpartner */}
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <User className="w-5 h-5 text-brand-green" />
                  Ansprechpartner
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vorname *</label>
                    <input
                      type="text"
                      required
                      value={formData.contact_first_name}
                      onChange={(e) => setFormData({...formData, contact_first_name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nachname *</label>
                    <input
                      type="text"
                      required
                      value={formData.contact_last_name}
                      onChange={(e) => setFormData({...formData, contact_last_name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                  <input
                    type="text"
                    value={formData.contact_position}
                    onChange={(e) => setFormData({...formData, contact_position: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    placeholder="Geschäftsführer, Werkstattleiter, etc."
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefon *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Außenwerbung */}
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Ruler className="w-5 h-5 text-brand-green" />
                  Außenwerbung (Plane)
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Breite (in cm)</label>
                    <input
                      type="text"
                      value={formData.plane_width}
                      onChange={(e) => setFormData({...formData, plane_width: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      placeholder="z.B. 300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Höhe (in cm)</label>
                    <input
                      type="text"
                      value={formData.plane_height}
                      onChange={(e) => setFormData({...formData, plane_height: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      placeholder="z.B. 100"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Anbringungsort</label>
                  <input
                    type="text"
                    value={formData.plane_location}
                    onChange={(e) => setFormData({...formData, plane_location: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    placeholder="z.B. Fassade über Eingang, Zaun links, etc."
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="plane_photo"
                    checked={formData.plane_photo_permission}
                    onChange={(e) => setFormData({...formData, plane_photo_permission: e.target.checked})}
                    className="w-5 h-5 text-brand-green rounded"
                  />
                  <label htmlFor="plane_photo" className="text-sm text-gray-700">
                    Ich bin einverstanden, nach Anbringung ein Foto zu senden
                  </label>
                </div>
              </div>

              {/* Vertriebler */}
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-brand-green" />
                  Vertriebler-Notizen
                </h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name des Vertrieblers *</label>
                  <input
                    type="text"
                    required
                    value={formData.sales_rep_name}
                    onChange={(e) => setFormData({...formData, sales_rep_name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notizen / Bemerkungen</label>
                  <textarea
                    value={formData.sales_rep_notes}
                    onChange={(e) => setFormData({...formData, sales_rep_notes: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                    placeholder="Zusätzliche Informationen, Gesprächsnotizen, etc."
                  />
                </div>
              </div>

              {/* Status */}
              <div className="space-y-4 bg-gray-50 rounded-xl p-6">
                <h2 className="text-lg font-bold text-gray-900">Entscheidung</h2>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="decision"
                      checked={formData.interested}
                      onChange={() => setFormData({...formData, interested: true, contact_later: false})}
                      className="w-5 h-5 text-brand-green"
                    />
                    <span className="text-gray-700">
                      <strong className="text-brand-green">Ja, Partner werden!</strong> – Bitte Zugangsdaten zusenden
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="decision"
                      checked={formData.contact_later}
                      onChange={() => setFormData({...formData, interested: false, contact_later: true})}
                      className="w-5 h-5 text-brand-green"
                    />
                    <span className="text-gray-700">
                      <strong>Interesse, aber später kontaktieren</strong>
                    </span>
                  </label>
                </div>
              </div>

              {/* Zustimmung */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  required
                  checked={formData.terms_accepted}
                  onChange={(e) => setFormData({...formData, terms_accepted: e.target.checked})}
                  className="w-5 h-5 text-brand-green rounded mt-1"
                />
                <label htmlFor="terms" className="text-sm text-gray-700">
                  Ich bin einverstanden, dass meine Daten zur Kontaktaufnahme und Partnerschaftsanbahnung 
                  verwendet werden. Die Datenschutzerklärung habe ich zur Kenntnis genommen. *
                </label>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting || (!formData.interested && !formData.contact_later)}
                className="w-full bg-brand-green hover:bg-brand-green-dark text-white py-6 text-lg font-bold rounded-xl"
              >
                {submitting ? (
                  'Wird übermittelt...'
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Daten übermitteln
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Helmet>
        <title>Partner-Präsentation | Dein Gutachten Direkt</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_DARK_BG} alt="Dein Gutachten Direkt" className="h-10" />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-300">
              Folie {currentSlide + 1} von {slides.length}
            </span>
            <a href={`tel:${COMPANY_INFO.phone}`} className="flex items-center gap-2 text-brand-green font-semibold">
              <Phone className="w-4 h-4" />
              {COMPANY_INFO.phoneDisplay}
            </a>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-slate-700 h-1">
        <div 
          className="bg-brand-green h-1 transition-all duration-300"
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 min-h-[600px]">
          {/* Slide Title */}
          <div className="text-center mb-8">
            <p className="text-brand-green font-semibold text-sm uppercase tracking-wider mb-2">
              {slides[currentSlide].title}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              {slides[currentSlide].subtitle}
            </h1>
          </div>

          {/* Slide Content */}
          <div className="mb-8">
            {slides[currentSlide].content}
          </div>
        </div>
      </main>

      {/* Navigation Footer - z-50 to appear above cookie banner */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-5 h-5" />
            Zurück
          </Button>

          {/* Slide Indicators */}
          <div className="flex gap-2 items-center">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  idx === currentSlide ? 'bg-brand-green' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
            <span className="ml-4 text-xs text-gray-400">{getVersionString()}</span>
          </div>

          <Button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className="bg-brand-green hover:bg-brand-green-dark text-white flex items-center gap-2"
          >
            Weiter
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default VertriebPraesentationPage;
